import React, { useEffect, useState } from "react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/calcEngine";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { School, ShieldCheck, KeyRound, Eye, EyeOff, Check, Lightbulb } from "lucide-react";

const CAT_COLOR = { balvatika: "#DB2777", primary: "#2563EB", upper_primary: "#059669", high_school: "#EA580C" };
const SUGG_BADGE = { "New": "bg-blue-100 text-blue-700", "Under review": "bg-amber-100 text-amber-700", "Planned": "bg-purple-100 text-purple-700", "Done": "bg-green-100 text-green-700", "Not possible": "bg-red-100 text-red-700" };

export default function Profile() {
  const { t } = useLang();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [mySugg, setMySugg] = useState([]);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      const pr = data.profile || {};
      setP({ school_name: "", udise: "", block: "", district: "", hm_name: "", village: "", kitchen_type: "", ngo_shg: "", school_type: "", category: "", ...pr, enrolled: { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0, ...(pr.enrolled || {}) } });
    });
    api.get("/suggestions/mine").then(({ data }) => setMySugg(data.suggestions || [])).catch(() => {});
  }, []);

  if (!p) return <Loader />;

  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", p); toast.success("Profile saved!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match.");
    setPwBusy(true);
    try {
      await api.post("/profile/change-password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Password changed!");
      setPw({ old_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  const F = (label, key, extra = {}) => (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input className="input-thali mt-1" value={p[key] || ""} onChange={(e) => setP({ ...p, [key]: e.target.value })} data-testid={`profile-${key}`} {...extra} />
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">{t("profile")}</h1>

      <div className="card-thali space-y-4">
        <div className="flex items-center gap-2 font-heading font-bold text-lg text-thali-green"><School className="w-5 h-5" /> School Details</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {F(t("school_name"), "school_name")}
          {F(t("udise"), "udise", { inputMode: "numeric" })}
          {F(t("block"), "block")}
          {F(t("district"), "district")}
          {F("Village / Ward", "village")}
          {F(t("hm_name"), "hm_name")}
          {F("Kitchen Type", "kitchen_type")}
          {F("NGO / SHG", "ngo_shg")}
          <div>
            <label className="text-sm font-semibold">School Type</label>
            <select className="input-thali mt-1" value={p.school_type || ""} onChange={(e) => setP({ ...p, school_type: e.target.value })} data-testid="profile-school_type">
              <option value="">Select…</option>
              {["Government", "Local Body", "EGS/AIE Centers", "NCLP", "Madras / Maqtab"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Category (MDCF)</label>
            <select className="input-thali mt-1" value={p.category || ""} onChange={(e) => setP({ ...p, category: e.target.value })} data-testid="profile-category">
              <option value="">Auto (from enrolment)</option>
              {["Primary", "Upper Primary", "Primary with Upper Primary"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold">{t("enrolled")}</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {CATEGORIES.map((c) => (
              <div key={c} className="flex items-center justify-between rounded-xl bg-white border border-thali-turmeric/30 p-2 pl-3" style={{ borderLeft: `5px solid ${CAT_COLOR[c]}` }}>
                <span className="text-sm font-semibold">{t(c)}</span>
                <input value={p.enrolled[c]} inputMode="numeric" className="w-14 text-center font-num font-bold bg-transparent border-b border-thali-turmeric/40"
                  onChange={(e) => setP({ ...p, enrolled: { ...p.enrolled, [c]: parseInt(e.target.value || 0, 10) || 0 } })} data-testid={`profile-enrolled-${c}`} />
              </div>
            ))}
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-3d-green w-full" data-testid="profile-save">{saving ? "…" : t("save")}</button>
      </div>

      <div className="card-thali space-y-3">
        <div className="flex items-center gap-2 font-heading font-bold text-lg text-thali-green"><KeyRound className="w-5 h-5" /> {t("change_password")}</div>
        <div className="relative">
          <input className="input-thali pr-12" type={show ? "text" : "password"} placeholder="Current password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} data-testid="pw-old" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-thali-ink/50">{show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
        </div>
        <input className="input-thali" type={show ? "text" : "password"} placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
        <input className="input-thali" type={show ? "text" : "password"} placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" />
        <button onClick={changePw} disabled={pwBusy} className="btn-3d-saffron w-full" data-testid="pw-submit">{pwBusy ? "…" : t("change_password")}</button>
      </div>

      <div className="card-thali flex items-center justify-between">
        <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-thali-green" /> <span className="font-semibold">Password Recovery</span></div>
        <span className={`text-sm font-bold flex items-center gap-1 ${user?.recovery_complete ? "text-thali-green" : "text-thali-saffron"}`}>
          {user?.recovery_complete ? <><Check className="w-4 h-4" /> Set up</> : "Not set up"}
        </span>
      </div>

      {mySugg.length > 0 && (
        <div className="card-thali" data-testid="my-suggestions">
          <div className="flex items-center gap-2 font-heading font-bold text-lg text-thali-green mb-2"><Lightbulb className="w-5 h-5" /> My Suggestions</div>
          <div className="space-y-2">
            {mySugg.map((s) => (
              <div key={s.id} className="rounded-xl bg-thali-cream p-3" data-testid={`my-sugg-${s.id}`}>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold">{s.category}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${SUGG_BADGE[s.status] || "bg-white"}`}>{s.status}</span>
                </div>
                <p className="text-sm text-thali-ink/70 mt-1">{s.message}</p>
                {s.admin_reply && <div className="text-sm mt-1 bg-white rounded-lg p-2"><b>Reply:</b> {s.admin_reply}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
